import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  Sparkles,
  CheckCircle,
  Clock,
  Target,
  Heart,
  Zap,
  Eye,
  Share2,
  Download,
  ChevronLeft,
  ChevronRight,
  Brain,
  Activity,
  Layers,
  Cpu,
  Search,
  Shield,
} from "lucide-react";
import PaymentModal from "./PaymentModal";

// Smooth scroll utility
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

// Enhanced Loading Screen Component
const LoadingScreen = ({ firstName }) => {
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [subMessage, setSubMessage] = useState("");
  const [dots, setDots] = useState("");

  // Analysis phases with progress ranges
  const phases = [
    {
      icon: Brain,
      title: "Initializing Neural Analysis",
      messages: [
        "Loading psychological frameworks...",
        "Preparing neuroscience models...",
        "Activating pattern recognition systems...",
      ],
      progressStart: 0,
      progressEnd: 15,
    },
    {
      icon: Search,
      title: "Deep Pattern Recognition",
      messages: [
        "Analyzing response patterns...",
        "Identifying core themes...",
        "Mapping emotional signatures...",
      ],
      progressStart: 15,
      progressEnd: 35,
    },
    {
      icon: Layers,
      title: "Psychological Profiling",
      messages: [
        "Processing with Jungian archetypes...",
        "Applying cognitive behavioral insights...",
        "Cross-referencing personality matrices...",
      ],
      progressStart: 35,
      progressEnd: 55,
    },
    {
      icon: Activity,
      title: "Neuroscience Integration",
      messages: [
        "Analyzing neurological response patterns...",
        "Mapping dopamine motivation pathways...",
        "Identifying cognitive strengths...",
      ],
      progressStart: 55,
      progressEnd: 75,
    },
    {
      icon: Cpu,
      title: "Purpose Algorithm Processing",
      messages: [
        "Running deep learning models...",
        "Synthesizing 10,000+ data points...",
        "Calculating purpose alignment scores...",
      ],
      progressStart: 75,
      progressEnd: 90,
    },
    {
      icon: Shield,
      title: "Finalizing Your Profile",
      messages: [
        "Generating personalized insights...",
        "Creating your unique purpose map...",
        "Preparing recommendations...",
      ],
      progressStart: 90,
      progressEnd: 100,
    },
  ];

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Progress animation
  useEffect(() => {
    const duration = 105000; // ~105s average to reflect 1–2 minutes
    const increment = 100 / (duration / 100); // Update every 100ms

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const next = Math.min(prev + increment, 100);

        // Update phase based on progress
        const newPhase = phases.findIndex(
          (phase) => next >= phase.progressStart && next < phase.progressEnd
        );
        if (newPhase !== -1 && newPhase !== currentPhase) {
          setCurrentPhase(newPhase);
          setSubMessage("");
        }

        return next;
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [currentPhase]);

  // Cycle through sub-messages
  useEffect(() => {
    if (phases[currentPhase]) {
      let messageIndex = 0;
      setSubMessage(phases[currentPhase].messages[0]);

      const messageInterval = setInterval(() => {
        messageIndex =
          (messageIndex + 1) % phases[currentPhase].messages.length;
        setSubMessage(phases[currentPhase].messages[messageIndex]);
      }, 3000);

      return () => clearInterval(messageInterval);
    }
  }, [currentPhase]);

  const CurrentIcon = phases[currentPhase]?.icon || Brain;
  // Estimate seconds remaining based on the same duration model
  const timeRemaining = Math.max(0, Math.round((100 - progress) * 1.05));

  return (
    <div className="min-h-screen gradient-bg py-6 px-4 page-transition">
      <div className="max-w-2xl mx-auto">
        <div className="glass rounded-2xl shadow-2xl p-6 sm:p-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="relative inline-block mb-4">
              {/* Animated rings */}
              <div className="absolute inset-0 animate-ping">
                <div className="w-20 h-20 mx-auto bg-purple-200 rounded-full opacity-20"></div>
              </div>
              <div className="absolute inset-0 animate-ping animation-delay-200">
                <div className="w-20 h-20 mx-auto bg-purple-300 rounded-full opacity-15"></div>
              </div>

              {/* Icon container */}
              <div className="relative w-20 h-20 mx-auto bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center shadow-lg transform rotate-3 hover:rotate-6 transition-transform">
                <CurrentIcon className="w-10 h-10 text-white animate-pulse" />
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-2">
              {firstName ? `${firstName}, w` : "W"}e're creating something
              special{dots}
            </h2>
            <p className="text-sm text-gray-600 mb-1">
              Our advanced AI is conducting a deep psychological analysis
            </p>
          </div>

          {/* Current Phase Display */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">
                {phases[currentPhase]?.title}
              </h3>
              <span className="text-sm font-medium text-purple-600">
                {Math.round(progress)}%
              </span>
            </div>

            {/* Progress Bar */}
            <div className="relative">
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-300 ease-out relative overflow-hidden"
                  style={{ width: `${progress}%` }}
                >
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                </div>
              </div>

              {/* Phase indicators */}
              <div className="absolute top-0 left-0 w-full h-3 flex">
                {phases.map((phase, index) => (
                  <div
                    key={index}
                    className="border-r border-gray-300 last:border-r-0"
                    style={{
                      width: `${phase.progressEnd - phase.progressStart}%`,
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Sub-message */}
            <p className="text-xs text-gray-500 mt-2 h-4 transition-all">
              {subMessage}
            </p>
          </div>

          {/* Analysis Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {phases.map((phase, index) => {
              const PhaseIcon = phase.icon;
              const isActive = index === currentPhase;
              const isComplete = progress > phase.progressEnd;

              return (
                <div
                  key={index}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    isComplete
                      ? "bg-green-50 border-green-300"
                      : isActive
                      ? "bg-purple-50 border-purple-400 shadow-md scale-105"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <PhaseIcon
                      className={`w-4 h-4 ${
                        isComplete
                          ? "text-green-600"
                          : isActive
                          ? "text-purple-600 animate-pulse"
                          : "text-gray-400"
                      }`}
                    />
                    <span
                      className={`text-xs font-medium ${
                        isComplete
                          ? "text-green-700"
                          : isActive
                          ? "text-purple-700"
                          : "text-gray-500"
                      }`}
                    >
                      Phase {index + 1}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Science badges */}
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              <Brain size={12} />
              Neuroscience-Based
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              <Activity size={12} />
              10,000+ Data Points
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              <Shield size={12} />
              Evidence-Based
            </span>
          </div>

          {/* Bottom info */}
          <div className="text-center">
            <p className="text-xs text-gray-500">
              Estimated time remaining: {timeRemaining} seconds
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              This deep analysis typically takes 1-2 minutes
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Results = ({
  report,
  firstName,
  onBack,
  onStartOver,
  isGenerating = false,
  generationError = null,
  reportGeneratedAt = null,
}) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  // Local preview flag: appending ?previewOffer=1 to the URL will force the
  // offer card to render without completing the assessment. This is useful for
  // local/dev review of the offer design and flow.
  const previewOffer =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("previewOffer") === "1";

  // Calculate offer end time based on when the report was generated
  const [offerEndAtMs, setOfferEndAtMs] = useState(() => {
    // If we have a report generation timestamp, use it to calculate offer end
    if (reportGeneratedAt) {
      return reportGeneratedAt + 25 * 60 * 1000; // 25 minutes from generation
    }
    // Fallback for new generations
    return Date.now() + 25 * 60 * 1000;
  });

  const [timeLeftMs, setTimeLeftMs] = useState(() =>
    Math.max(offerEndAtMs - Date.now(), 0)
  );
  const isOfferActive = previewOffer || timeLeftMs > 0;

  // Update offer end time if reportGeneratedAt changes
  useEffect(() => {
    if (reportGeneratedAt) {
      const newOfferEnd = reportGeneratedAt + 25 * 60 * 1000;
      setOfferEndAtMs(newOfferEnd);
      setTimeLeftMs(Math.max(newOfferEnd - Date.now(), 0));
    }
  }, [reportGeneratedAt]);

  // Countdown timer
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeftMs(Math.max(offerEndAtMs - Date.now(), 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [offerEndAtMs]);

  const formatHms = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
      2,
      "0"
    );
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  // Simulated dynamic spots remaining
  const [spotsLeft, setSpotsLeft] = useState(() => {
    // Calculate spots based on how long ago the report was generated
    if (reportGeneratedAt) {
      const minutesSinceGeneration = (Date.now() - reportGeneratedAt) / 60000;
      // Decrease spots by 1 for every 2 minutes passed, minimum 11
      const spotsReduction = Math.floor(minutesSinceGeneration / 2);
      return Math.max(11, 47 - spotsReduction);
    }
    return 47;
  });

  useEffect(() => {
    // Decay every 45-90s by 1 until floor of 11
    if (!isOfferActive || spotsLeft <= 11) return;
    const delay = 45000 + Math.floor(Math.random() * 45000);
    const t = setTimeout(() => setSpotsLeft((n) => Math.max(11, n - 1)), delay);
    return () => clearTimeout(t);
  }, [spotsLeft, isOfferActive]);

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    console.log("Payment successful!");
  };

  // Navigation functions with scroll to top
  const handleNext = () => {
    scrollToTop();
    const reportSections = parseReport(report);
    const totalSections = reportSections ? reportSections.length + 1 : 1;
    if (currentSection < totalSections - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const handlePrevious = () => {
    scrollToTop();
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  if (isGenerating) {
    return <LoadingScreen firstName={firstName} />;
  }

  if (generationError) {
    return (
      <div className="min-h-screen gradient-bg py-6 px-4 page-transition">
        <div className="max-w-lg mx-auto">
          <div className="glass rounded-xl shadow-xl p-6 sm:p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 bg-red-100 rounded-xl flex items-center justify-center">
              <Clock className="w-7 h-7 text-red-600" />
            </div>
            <h2 className="text-xl font-medium text-gray-900 mb-3">
              Report Generation Failed
            </h2>
            <p className="text-sm text-gray-600 mb-5">
              We encountered an issue generating your report. Please try again.
            </p>
            <div className="space-y-2">
              <button
                onClick={onBack}
                className="w-full premium-button text-white rounded-lg py-2.5 px-5 font-semibold text-sm hover-lift flex items-center justify-center gap-2"
              >
                <ArrowLeft size={16} />
                <span>Try Again</span>
              </button>
              <button
                onClick={onStartOver}
                className="w-full bg-gray-100 text-gray-700 rounded-lg py-2.5 px-5 font-semibold text-sm hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
              >
                <span>Start Over</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Parse the report content into sections
  const parseReport = (reportText) => {
    if (!reportText) return null;

    const sections = reportText.split(/(?=^## )/m);

    return sections
      .map((section, index) => {
        const lines = section.trim().split("\n");
        const title = lines[0].replace(/^## /, "");
        const content = lines.slice(1).join("\n").trim();

        return { title, content, index };
      })
      .filter((section) => section.title && section.content);
  };

  // In preview mode we don't need actual report sections; treat as empty so we
  // can jump straight to the offer card.
  const reportSections = previewOffer ? [] : parseReport(report);
  const isOnOfferSection =
    currentSection >= (reportSections ? reportSections.length : 0);

  // No effect needed: in preview mode, reportSections is empty, so the
  // renderer naturally shows the offer card without navigating sections.

  return (
    <div className="min-h-screen gradient-bg py-6 px-4 page-transition">
      <div className="max-w-3xl mx-auto">
        {/* Header - hidden on offer section */}
        {!isOnOfferSection && (
          <div className="mb-4">
            <div className="text-center">
              {currentSection === 0 && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 rounded-full text-green-700 text-xs font-medium mb-3">
                  <CheckCircle size={12} />
                  <span>Report Generated</span>
                </div>
              )}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
                <span className="gradient-text">Your Purpose Profile</span>
                <br />
                <span className="text-gray-900 text-xl sm:text-2xl">Mini</span>
              </h1>
              {/* <p className="text-xs sm:text-sm text-gray-600 max-w-lg mx-auto">
                Based on your responses, here's a glimpse into the deeper patterns
                that might be shaping your life.
              </p> */}

              {/* Show when report was generated if it's been a while */}
              {reportGeneratedAt && Date.now() - reportGeneratedAt > 60000 && (
                <p className="text-xs text-gray-500 mt-2">
                  Report generated{" "}
                  {Math.floor((Date.now() - reportGeneratedAt) / 60000)} minutes
                  ago
                </p>
              )}
            </div>
          </div>
        )}

        {/* Current Section Content */}
        <div className="mb-4">
          {currentSection < reportSections.length ? (
            // Show report section - Mobile optimized
            <div className="glass rounded-xl shadow-lg p-4 sm:p-6 animate-fadeIn">
              <div className="mb-3">
                <h2 className="text-lg sm:text-xl md:text-2xl font-medium text-gray-900 mb-2">
                  {reportSections[currentSection].title}
                </h2>
                <div className="w-10 h-0.5 bg-purple-500 rounded-full"></div>
              </div>

              <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => (
                      <h1 className="text-xl sm:text-2xl font-medium text-gray-900 mb-3 mt-4">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-lg sm:text-xl font-medium text-gray-900 mb-2 mt-4">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 mt-3">
                        {children}
                      </h3>
                    ),
                    p: ({ children }) => (
                      <p className="mb-3 text-sm sm:text-base text-gray-700 leading-relaxed">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-gray-900">
                        {children}
                      </strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic text-gray-800">{children}</em>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside mb-3 space-y-1 text-sm sm:text-base text-gray-700">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside mb-3 space-y-1 text-sm sm:text-base text-gray-700">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="text-sm sm:text-base text-gray-700">
                        {children}
                      </li>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-3 border-purple-200 pl-3 italic text-sm sm:text-base text-gray-600 my-3">
                        {children}
                      </blockquote>
                    ),
                    code: ({ children }) => (
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono text-gray-800">
                        {children}
                      </code>
                    ),
                    pre: ({ children }) => (
                      <pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto text-xs">
                        {children}
                      </pre>
                    ),
                  }}
                >
                  {reportSections[currentSection].content}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            // Show offer card - Mobile optimized to match landing page
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 animate-fadeIn">
              {/* Limited Time Banner */}
              <div className="flex justify-center mb-4">
                <div className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  LIMITED TIME: 95% OFF
                </div>
              </div>

              {/* Urgency chip (timer only) */}
              <div className="flex items-center justify-center gap-2 mb-4">
                {isOfferActive ? (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded-full"
                    aria-live="polite"
                  >
                    <Clock className="w-3 h-3" /> {formatHms(timeLeftMs)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] text-center font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                    Discount expired — full profile + strategy session still available for $47
                  </span>
                )}
              </div>

              {/* Main Heading */}
              <h3 className="text-xl sm:text-2xl font-medium text-gray-900 text-center mb-2">
                Everything you get today
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Go from stuck to clear (for less than dinner).
              </p>

              {/* Individual Benefit Cards */}
              <div className="space-y-2.5 mb-2">
                {/* AI Voice Analysis Session */}
                <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">AI Voice Analysis Session (25 prompts)</h4>
                    <p className="text-xs text-gray-600 mt-1">Replacing 20+ hours of solo work at your $$ hourly rate.</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-purple-700">$147</div>
                  </div>
                </div>
                {/* minimal divider */}
                <div className="h-px w-5/6 bg-gray-200/70 mx-auto" />

                {/* Purpose Profile Report */}
                <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">Your Purpose Profile Report</h4>
                    <p className="text-xs text-gray-600 mt-1">Clarity of 6–8 coaching sessions in one report.</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-purple-700">$197</div>
                  </div>
                </div>
                {/* minimal divider */}
                <div className="h-px w-5/6 bg-gray-200/70 mx-auto" />

                {/* Money-Back Guarantee */}
                <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">100% Money-Back Guarantee</h4>
                    <p className="text-xs text-gray-600 mt-1">If it’s not valuable, you get a full refund.</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-green-600">Included</div>
                  </div>
                </div>
                {/* minimal divider */}
                <div className="h-px w-5/6 bg-gray-200/70 mx-auto" />

                {/* Bonus Strategy Session */}
                <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3 relative">
                  <div className="absolute -top-1 -right-1 bg-purple-700 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                    BONUS
                  </div>
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">BONUS: Free 1-on-1 Strategy Session</h4>
                    <p className="text-xs text-gray-600 mt-1">60‑min 1‑on‑1 with a founder to plan your next steps.</p>
                    <div className="mt-2">
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded-full">
                        <Zap className="w-3 h-3" /> {spotsLeft} spots left
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-purple-700">$197</div>
                  </div>
                </div>
              </div>

              {/* Value Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center text-sm mb-2">
                  <span className="text-gray-600">Total Value:</span>
                  <span className="text-gray-900 font-medium">= $568</span>
                </div>
                <div className="flex justify-between items-center text-sm mb-2">
                  <span className="text-gray-600">You Save:</span>
                  <span className="text-green-600 font-medium">- $541</span>
                </div>
                <div className="border-t border-gray-200 pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-900 font-medium">Your Price Today:</span>
                    <span className="text-2xl font-bold text-purple-700">${isOfferActive ? "27" : "47"}</span>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="mb-6">
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className={`w-full rounded-lg py-3 px-4 font-semibold text-sm flex items-center justify-center gap-2 text-white hover-lift ${
                    isOfferActive
                      ? "premium-button"
                      : "bg-gray-900 hover:bg-gray-800 transition-colors"
                  }`}
                  aria-label={`Get Instant Access for $${isOfferActive ? "27" : "47"}`}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
                  </svg>
                  <span>Get Instant Access — ${isOfferActive ? "27" : "47"}</span>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                </button>
              </div>

              {/* Social Proof */}
              <div className="text-center mb-4">
                <div className="flex justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-500 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-sm text-gray-600">4.9/5 from 100+ users who found their path</p>
              </div>

              {/* Guarantees */}
              <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-gray-600">
                <div className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span>One-time payment</span>
                </div>
                <div className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span>Instant access (start in 2 min)</span>
                </div>
                <div className="inline-flex items-center gap-1">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                  </svg>
                  <span>Secure payment by Stripe</span>
                </div>
              </div>

              {/* Testimonials Section - Mobile Optimized */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-4 text-center">
                  See what others have to say:
                </h4>
                
                {/* Mobile-first testimonial layout */}
                <div className="space-y-4">
                  {/* Testimonial 1 - Sophie */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <img
                          src="/sophie.jpg"
                          alt="Sophie-Anne Onland"
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML = `
                              <div class="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-xs">
                                SO
                              </div>
                            `;
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              Sophie-Anne Onland
                            </p>
                            
                          </div>
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className="w-3 h-3 text-yellow-500 fill-current"
                                viewBox="0 0 20 20"
                              >
                                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs font-medium text-purple-700 mb-1">
                          From confusion to crystal-clear direction
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          I was at a crossroads needing clarity. The assessment
                          revealed surprising truths about what work suits me.
                          Four months later, I'm still actively pursuing the goals
                          I set.
                        </p>
                        <p className="text-[11px] text-gray-600 italic font-thin">Founder, SOOPH</p>
                      </div>
                    </div>
                  </div>

                  {/* Testimonial 2 - Tony */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <img
                          src="/tony.jpg"
                          alt="Tony van der Zanden"
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML = `
                              <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white font-semibold text-xs">
                                TV
                              </div>
                            `;
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              Tony van der Zanden
                            </p>
                           
                          </div>
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className="w-3 h-3 text-yellow-500 fill-current"
                                viewBox="0 0 20 20"
                              >
                                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs font-medium text-purple-700 mb-1">
                          Finally understood who I really am
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          The assessment revealed my strengths and blind spots. I
                          experience more flow, confidence, and meaning. Now everything
                          feels more aligned with who I actually am.
                        </p>
                        <p className="text-[11px] text-gray-600 italic font-thin">Founder, Hiking Platform</p>
                      </div>
                    </div>
                  </div>

                  {/* Testimonial 3 - Luuk */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <img
                          src="/luuk.png"
                          alt="Luuk Alleman"
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML = `
                              <div class="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-400 flex items-center justify-center text-white font-semibold text-xs">
                                LA
                              </div>
                            `;
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              Luuk Alleman
                            </p>
                            
                          </div>
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className="w-3 h-3 text-yellow-500 fill-current"
                                viewBox="0 0 20 20"
                              >
                                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs font-medium text-purple-700 mb-1">
                          Building a business that truly fits
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          What clicked was connecting my personality to concrete
                          decisions. Now I'm building something that gives me
                          energy every single day.
                        </p>
                        <p className="text-[11px] text-gray-600 italic font-thin">Founder, Everyman AI</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons - Mobile optimized */}
        {reportSections && reportSections.length > 0 && (
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={handlePrevious}
              disabled={currentSection === 0}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-medium"
            >
              <ChevronLeft size={16} />
              <span>Previous</span>
            </button>

            {/* Section indicator */}
            <div className="text-xs text-gray-500">
              {currentSection + 1} / {reportSections.length + 1}
            </div>

            {/* Only show Next button when not on offer section */}
            {currentSection < reportSections.length && (
              <button
                onClick={handleNext}
                className="flex items-center gap-1 px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm font-medium hover-lift"
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        productName="Full Purpose Profile"
        priceId={
          isOfferActive
            ? "price_1S79oRLLcWiUuGOnXX00XNqn"
            : "price_1RqCEXLLcWiUuGOnXBb1ZfI6"
        }
      />
    </div>
  );
};

export default Results;
